// HRBonusSrazkaAddModal — modal pro pridani bonusu nebo srazky.
//
// Workflow:
//   1. Typ je predvyplneny z defaultTyp (z tlacitka v hlavicce).
//   2. Vybereš osobu, datum (default dnes), popis (textarea), castku.
//   3. Cilovy stav: manager VZDY 'navrh' (nemenitelne). Owner muze:
//      - navrh (rezerva, kdyby chtel jeste resit)
//      - caka_splneni (podmineny bonus, ceka na klienta)
//      - caka_vyplaceni (rovnou schvaleno)
//      - vyplacen (rovnou proplaceno, vyzaduje mesic_vyplaceni)
//   4. Klik "Pridat" → POST /api/hr-bs-add.

import React, { useEffect, useMemo, useRef, useState } from "react";
import useEscClose from "../lib/useEscClose.js";
import { Modal, HlavickaOkna } from "./ui.jsx";
import { Plus, Award, Banknote, Loader2, AlertCircle, TrendingUp, TrendingDown, Search, Check } from "lucide-react";
import { apiFetch, useApiData } from "../lib/api.js";
import { getCurrentUser } from "../lib/auth.js";

function normalize(s) {
  return String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();
}

const MONTHS_CS = ["Leden","Únor","Březen","Duben","Květen","Červen","Červenec","Srpen","Září","Říjen","Listopad","Prosinec"];
function mesicLabel(m) {
  if (!m || !/^\d{4}-\d{2}$/.test(m)) return m || "—";
  const [y, mm] = m.split("-");
  return `${MONTHS_CS[parseInt(mm, 10) - 1]} ${y}`;
}

// 28.5.2026 v3: 6 stavu workflow.
// Pri INSERT manager i owner: caka_splneni (default) nebo splneno_caka_schvaleni.
// Owner navic: vyplacen (rovnou proplaceno mimo workflow).
const STAV_OPTIONS = [
  { value: "caka_splneni", label: "Čeká na splnění podmínky", hint: "Bonus nebo srážka závisí na události u klienta (prodloužení, obrat, udržení atd.). Až nastane, klikni Splněno." },
  { value: "splneno_caka_schvaleni", label: "Rovnou splněno (čeká schválení ownera)", hint: "Podmínka už není potřeba. Položka jde rovnou ownerovi ke schválení vyplacení." },
];
const STAV_OPTION_VYPLACEN = { value: "vyplacen", label: "Rovnou vyplaceno (jen owner)", hint: "Položka už byla proplacena. Vyplň měsíc proplacení." };

// 28.5.2026 v4: predpripravene sablony bonusu pro rychly vyber.
// Po kliku se predpilni popis, castka a doporucenz stav. Vse lze pred ulozenim upravit.
const SABLONY_BONUS = [
  {
    key: "uzavreni_gold_premium",
    label: "Uzavření klienta GOLD/PREMIUM",
    castka: 500,
    popis: "Uzavření klienta na zkušebním tréninku (GOLD nebo PREMIUM)",
    defaultStav: "caka_splneni",
    podminky: "Klient podepíše smlouvu hned po tréninku a je přidán do Telegramu do týdne. Bonus se vyplácí standardně.",
    vyzaduje_klienta: true,
    klient_je_novy: false,
  },
  {
    key: "uzavreni_flexible",
    label: "Uzavření klienta FLEXIBLE",
    castka: 500,
    popis: "Uzavření klienta na zkušebním tréninku (FLEXIBLE)",
    defaultStav: "caka_splneni",
    podminky: "Klient podepíše smlouvu hned po tréninku a je přidán do Telegramu do týdne. POZOR: bonus se vyplatí AŽ PO ZAPLACENÍ druhého měsíce spolupráce (druhá faktura).",
    vyzaduje_klienta: true,
    klient_je_novy: false,
  },
  {
    key: "uzavreni_sleva_permanentka",
    label: "Uzavření klienta (sleva nebo Permanentka)",
    castka: 300,
    popis: "Uzavření klienta na zkušebním tréninku (sleva nebo Permanentka)",
    defaultStav: "caka_splneni",
    podminky: "Klient podepíše smlouvu hned po tréninku a je přidán do Telegramu do týdne. Při nedodržení bonus zaniká.",
    vyzaduje_klienta: true,
    klient_je_novy: false,
  },
  {
    key: "privedeni_klienta",
    label: "Přivedení nového klienta",
    castka: 2000,
    popis: "Přivedení nového klienta (např. stávající klient přivede kamarádku)",
    defaultStav: "caka_splneni",
    podminky: "Klient musí skutečně nastoupit a započít spolupráci. Při získání tohoto bonusu trenér nedostane bonus za klasické uzavření.",
    vyzaduje_klienta: true,
    klient_je_novy: true, // muze byt novy klient jeste neevidovany v DB → povol freetext
  },
  {
    key: "google_hodnoceni",
    label: "Hodnocení na Google",
    castka: null,
    popis: "Bonus za získání hodnocení klienta na Google",
    defaultStav: "splneno_caka_schvaleni",
    podminky: "Po zveřejnění hodnocení klientem. Částku vyplň podle dohody.",
    vyzaduje_klienta: false,
    klient_je_novy: false,
  },
];

export default function HRBonusSrazkaAddModal({ onClose, onSaved, defaultTyp = "bonus", defaultPersonId = null }) {
  useEscClose(onClose); // Esc zavre dialog
  const today = new Date().toISOString().substring(0, 10);
  const user = getCurrentUser();
  const isOwner = user?.role === "owner";

  const [typ, setTyp] = useState(defaultTyp);
  const isBonus = typ === "bonus";
  const [personId, setPersonId] = useState(defaultPersonId || "");
  const [datum, setDatum] = useState(today);
  const [popis, setPopis] = useState("");
  const [castka, setCastka] = useState("");
  const [stav, setStav] = useState("caka_splneni"); // 28.5.2026 v2: stejny default pro vsechny role
  const [mesicVyplaceni, setMesicVyplaceni] = useState(today.substring(0, 7));
  const [poznamka, setPoznamka] = useState("");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Combobox state — osoba
  const [personSearch, setPersonSearch] = useState("");
  const [personOpen, setPersonOpen] = useState(false);
  const personRef = useRef(null);

  // Aktivni sablona bonusu (klíč nebo null = vlastní)
  const [aktivniSablona, setAktivniSablona] = useState(null);
  const sablonaObj = aktivniSablona ? SABLONY_BONUS.find((s) => s.key === aktivniSablona) : null;
  const vyzadujeKlienta = isBonus && !!sablonaObj?.vyzaduje_klienta;
  const klientJeNovy = !!sablonaObj?.klient_je_novy;
  // Nepovinný klient u bonusu bez povinného klienta (vlastní popis, Google hodnocení):
  // jde vybrat z DB i napsat ručně, trenér ho uvidí v pilulce, do faktury se nepropíše.
  const klientVolitelny = isBonus && !vyzadujeKlienta;
  const povolRucniJmeno = klientJeNovy || klientVolitelny;

  // Klient (volitelne, ale pro DEAL sablony povinne)
  const [klientId, setKlientId] = useState(null);
  const [klientName, setKlientName] = useState("");      // bud z DB klienta NEBO freetext (novy klient)
  const [klientSearch, setKlientSearch] = useState("");
  const [klientOpen, setKlientOpen] = useState(false);
  // 16.7.2026: u klientských bonusů se hned ukazuje, jestli se klient
  // přihlásil do aplikace — bez přihlášení není nárok (server to i blokuje).
  const [loginInfo, setLoginInfo] = useState(null); // null=nezjišťováno | {prihlasen, kdy}
  // 18.7.2026: nepřihlášený klient bonus NEblokuje — jde založit jako
  // "čeká na splnění podmínky". Splněno/vyplaceno až po přihlášení (vynucuje
  // se i tady: stav se přepne a ostatní volby se zamknou).
  // 3.11.2026: přihlášení se hlídá jen u šablon, které klienta vyžadují (stejně jako server).
  // U vlastního bonusu je klient jen nepovinný údaj pro trenéra.
  const klientBezLoginu = vyzadujeKlienta && !!klientId && !!loginInfo && !loginInfo.prihlasen;
  useEffect(() => {
    if (klientBezLoginu && stav !== "caka_splneni") setStav("caka_splneni");
  }, [klientBezLoginu]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setLoginInfo(null);
    if (!klientId || !vyzadujeKlienta) return;
    let zij = true;
    apiFetch(`/api/hr-bs-add?loginCheck=1&klientId=${encodeURIComponent(klientId)}`)
      .then((r) => r.json())
      .then((j) => { if (zij && j && j.ok !== undefined) setLoginInfo({ prihlasen: !!j.prihlasen, kdy: j.kdy }); })
      .catch(() => {});
    return () => { zij = false; };
  }, [klientId, vyzadujeKlienta]);
  const klientRef = useRef(null);


  const applySablona = (s) => {
    setAktivniSablona(s.key);
    setPopis(s.popis);
    if (s.castka != null) setCastka(String(s.castka));
    setStav(s.defaultStav);
    // Šablona, která chce klienta z DB: ručně napsané jméno (z Vlastní nebo Přivedení) neplatí.
    if (s.vyzaduje_klienta && !s.klient_je_novy && !klientId) setKlientName("");
  };
  const resetSablona = () => {
    setAktivniSablona(null);
    setPopis("");
    setCastka("");
    setStav("caka_splneni");
    setKlientId(null);
    setKlientName("");
  };

  const { data: peopleData } = useApiData("/api/hr-list-people?filter=vse&status=aktivni");
  const people = peopleData?.people || [];
  const selectedPerson = people.find((p) => p.id === personId);

  // Klienti (light=ids endpoint vraci [{notion_id, klient_name, stav}]).
  // `klient` je fallback pro tvar plne odpovedi /api/clients — ta se sem 10.8.2026
  // dostavala z cache a nasptavac pak ukazoval jen "stav: ...", bez jmena.
  // Backend je opraveny, tohle je uz jen pojistka, at se prazdne jmeno nevykresli.
  const jmenoKlienta = (k) => String((k && (k.klient_name || k.klient)) || "").trim();
  const { data: clientsData } = useApiData("/api/clients?light=ids");
  const klienti = (clientsData?.clients || []).filter((k) => jmenoKlienta(k));

  const filteredPeople = useMemo(() => {
    const q = normalize(personSearch);
    if (!q) return people;
    return people.filter((p) => normalize(`${p.display_name} ${p.pozice} ${p.email || ""}`).includes(q));
  }, [people, personSearch]);

  const filteredKlienti = useMemo(() => {
    const q = normalize(klientSearch);
    if (!q) return klienti.slice(0, 100);
    return klienti.filter((k) => normalize(jmenoKlienta(k)).includes(q)).slice(0, 100);
  }, [klienti, klientSearch]);

  // Click-outside closes dropdown (oba comboboxy)
  useEffect(() => {
    function onDocClick(e) {
      if (personRef.current && !personRef.current.contains(e.target)) setPersonOpen(false);
      if (klientRef.current && !klientRef.current.contains(e.target)) setKlientOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Mesice pro vyplaceni (12 zpet, 3 dopredu — pro pozadnu fakturaci)
  const monthOptions = useMemo(() => {
    const arr = [];
    const now = new Date();
    for (let i = -3; i < 15; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      arr.push({ value: m, label: mesicLabel(m) });
    }
    return arr.sort((a, b) => (a.value < b.value ? 1 : -1));
  }, []);

  const handleSubmit = async () => {
    if (!personId) return setError("Vyber osobu.");
    if (!popis.trim()) return setError("Vyplň popis.");
    const cast = Number(castka);
    if (!Number.isFinite(cast) || cast <= 0) return setError("Částka musí být kladné číslo.");
    if (stav === "vyplacen" && !mesicVyplaceni) return setError("Pro stav Vyplaceno musíš vybrat měsíc proplacení.");
    if (vyzadujeKlienta && !klientId && (!klientJeNovy || !klientName.trim())) {
      return setError(klientJeNovy
        ? "Vyber klienta z databáze, nebo napiš jméno nového klienta ručně."
        : "Vyber klienta z databáze.");
    }
    // 18.7.2026: nepřihlášený klient → bonus jde založit JEN jako "čeká na splnění".
    if (klientBezLoginu && stav !== "caka_splneni") {
      return setError("Klient se do aplikace zatím nepřihlásil, bonus jde zadat jen jako „Čeká na splnění podmínky“.");
    }

    setSaving(true);
    setError(null);
    try {
      const body = {
        personId,
        typ,
        datum_vzniku: datum,
        popis: popis.trim(),
        castka: cast,
        stav,
        ...(stav === "vyplacen" ? { mesic_vyplaceni: mesicVyplaceni } : {}),
        ...(poznamka.trim() ? { poznamka: poznamka.trim() } : {}),
        ...(isBonus && klientId ? { klientId } : {}),
        ...(isBonus && klientName.trim() ? { klientName: klientName.trim() } : {}),
        ...(aktivniSablona ? { sablonaKod: aktivniSablona } : {}),
      };
      const r = await apiFetch("/api/hr-bs-add", { method: "POST", body: JSON.stringify(body) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      onSaved && onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Když je tlačítko zamčené, patka jasně řekne, co ještě chybí.
  const cast = Number(castka);
  const chybi = [];
  if (!personId) chybi.push("osoba");
  if (!popis.trim()) chybi.push("popis");
  if (!castka || !Number.isFinite(cast) || cast <= 0) chybi.push("částka");
  if (vyzadujeKlienta && !klientId && (!klientJeNovy || !klientName.trim())) chybi.push("klient");
  if (stav === "vyplacen" && !mesicVyplaceni) chybi.push("měsíc proplacení");

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      plnaHlavicka
      header={<HlavickaOkna ikona={isBonus ? Award : Banknote} nazev={`Přidat ${isBonus ? "bonus" : "srážku"}`} onClose={onClose} />}
      footer={
        <>
          {chybi.length > 0 ? (
            <div className="min-w-0 flex-1 rounded-xl border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-semibold text-amber-700">
              Ještě doplň: {chybi.join(", ")}
            </div>
          ) : (
            <div className="flex-1" />
          )}
          <button onClick={onClose} disabled={saving} className="btn btn-secondary shrink-0">Zrušit</button>
          <button
            onClick={handleSubmit}
            disabled={saving || chybi.length > 0}
            title={chybi.length > 0 ? `Ještě doplň: ${chybi.join(", ")}` : "Přidat položku"}
            className={`btn shrink-0 text-white ${isBonus ? "bg-emerald-600 hover:bg-emerald-500" : "bg-rose-600 hover:bg-rose-500"}`}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Přidat
          </button>
        </>
      }
    >
      <div className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Typ toggle */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Typ položky</label>
            <div className="inline-flex items-center gap-1 bg-slate-100 rounded-xl p-1 w-full">
              <button
                type="button"
                onClick={() => setTyp("bonus")}
                className={`flex-1 text-sm px-3 py-2 rounded-lg font-semibold transition-all flex items-center justify-center gap-1.5 ${
                  isBonus ? "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300" : "text-slate-600 hover:bg-slate-200"
                }`}
              >
                <TrendingUp className="w-4 h-4" /> Bonus (+)
              </button>
              <button
                type="button"
                onClick={() => { setTyp("srazka"); setAktivniSablona(null); }}
                className={`flex-1 text-sm px-3 py-2 rounded-lg font-semibold transition-all flex items-center justify-center gap-1.5 ${
                  !isBonus ? "bg-rose-100 text-rose-800 ring-1 ring-rose-300" : "text-slate-600 hover:bg-slate-200"
                }`}
              >
                <TrendingDown className="w-4 h-4" /> Srážka (−)
              </button>
            </div>
          </div>

          {/* Osoba — searchable combobox */}
          <div ref={personRef} className="relative">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Osoba</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={personOpen ? personSearch : (selectedPerson ? `${selectedPerson.display_name} (${selectedPerson.pozice})` : "")}
                onChange={(e) => { setPersonSearch(e.target.value); setPersonOpen(true); }}
                onFocus={() => { setPersonOpen(true); setPersonSearch(""); }}
                placeholder="Hledat jméno, pozici, email…"
                className="field w-full pl-10 pr-3"
              />
              {selectedPerson && !personOpen && (
                <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-600" />
              )}
            </div>
            {personOpen && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-72 overflow-y-auto">
                {filteredPeople.length === 0 && (
                  <div className="px-3 py-2.5 text-sm text-slate-500 italic">Žádná osoba neodpovídá.</div>
                )}
                {filteredPeople.map((p) => {
                  const isSel = p.id === personId;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => { setPersonId(p.id); setPersonOpen(false); setPersonSearch(""); }}
                      className={`w-full text-left px-3 py-2 flex items-center gap-2.5 hover:bg-brand-50 ${isSel ? "bg-brand-100" : ""} transition-colors`}
                    >
                      <ComboAvatar person={p} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate demo-sensitive">{p.display_name}</div>
                        <div className="text-[11px] text-slate-500">{p.pozice}</div>
                      </div>
                      {isSel && <Check className="w-4 h-4 text-brand-600 shrink-0" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Datum + Castka */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Datum vzniku</label>
              <input
                type="date"
                value={datum}
                onChange={(e) => setDatum(e.target.value)}
                className="field w-full"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Částka (Kč)</label>
              <input
                type="number"
                min="1"
                step="1"
                value={castka}
                onChange={(e) => setCastka(e.target.value)}
                placeholder="500"
                className="field w-full"
              />
            </div>
          </div>

          {/* Šablony bonusů (jen pro typ=bonus) */}
          {isBonus && (
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Šablona (volitelná)</label>
              <div className="grid grid-cols-2 gap-2">
                {SABLONY_BONUS.map((s) => {
                  const isActive = aktivniSablona === s.key;
                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => applySablona(s)}
                      className={`text-left p-2.5 rounded-xl border-2 transition-all ${
                        isActive
                          ? "border-emerald-400 bg-emerald-50 ring-2 ring-emerald-100"
                          : "border-slate-200 bg-white hover:border-emerald-300 hover:bg-slate-50"
                      }`}
                    >
                      <div className="text-sm font-semibold text-slate-800 leading-tight">{s.label}</div>
                      <div className="text-xs text-slate-500 mt-1 tabular-nums">
                        {s.castka != null ? `${s.castka} Kč` : "částka dle dohody"}
                      </div>
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={resetSablona}
                  className={`text-left p-2.5 rounded-xl border-2 border-dashed transition-all col-span-2 ${
                    aktivniSablona == null
                      ? "border-slate-400 bg-slate-50"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <div className="text-sm font-semibold text-slate-700">Vlastní (nevybrat šablonu)</div>
                  <div className="text-xs text-slate-500 mt-0.5">Vlastní popis a částku zadáš ručně níže.</div>
                </button>
              </div>
              {aktivniSablona && (
                <div className="text-[11px] text-slate-600 mt-2 p-2 bg-amber-50 border border-amber-200 rounded">
                  <b>Podmínky:</b> {SABLONY_BONUS.find((s) => s.key === aktivniSablona)?.podminky}
                </div>
              )}
            </div>
          )}

          {/* Klient combobox (zobrazeno pokud sablona vyzaduje, nebo manualne pro typ=bonus bez sablony) */}
          {isBonus && (
            <div ref={klientRef} className="relative">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">
                Klient {vyzadujeKlienta && <span className="text-rose-600">*</span>}
                {klientVolitelny && <span className="text-[11px] text-slate-400 font-normal normal-case ml-1">(nepovinné)</span>}
                {klientJeNovy && <span className="text-[11px] text-slate-400 font-normal normal-case ml-1">(vyber z DB, nebo napiš jméno nového klienta)</span>}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  value={klientOpen ? klientSearch : klientName}
                  onChange={(e) => {
                    setKlientSearch(e.target.value);
                    setKlientOpen(true);
                    if (!klientOpen) {
                      setKlientId(null);
                      setKlientName(e.target.value);
                    }
                  }}
                  onFocus={() => { setKlientOpen(true); setKlientSearch(klientName || ""); }}
                  placeholder={povolRucniJmeno ? "Hledat klienta, nebo napsat jméno…" : "Hledat klienta…"}
                  className="field w-full pl-10 pr-3"
                />
                {klientId && !klientOpen && (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-600" />
                )}
              </div>
              {vyzadujeKlienta && klientId && loginInfo && (
                loginInfo.prihlasen ? (
                  <div className="mt-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5">
                    Klient se do aplikace přihlásil{loginInfo.kdy ? ` (naposledy ${new Date(loginInfo.kdy).toLocaleDateString("cs-CZ")})` : ""}, nárok na bonus v pořádku.
                  </div>
                ) : (
                  <div className="mt-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 font-semibold">
                    ⏳ Klient se do aplikace zatím nepřihlásil, bonus se založí jako „Čeká na splnění podmínky“ a půjde označit jako splněný (a vyplatit) až po jeho přihlášení (SMS ověření). Do té doby zůstává vidět i v dalších měsících.
                  </div>
                )
              )}
              {!klientId && klientName.trim() && klientJeNovy && (
                <div className="mt-1.5 text-[11px] text-slate-500">
                  Nový klient mimo DB: přihlášení do aplikace se ověří při schvalování bonusu.
                </div>
              )}
              {klientOpen && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-64 overflow-y-auto">
                  {povolRucniJmeno && klientSearch.trim() && !filteredKlienti.some((k) => normalize(jmenoKlienta(k)) === normalize(klientSearch)) && (
                    <button
                      type="button"
                      onClick={() => { setKlientId(null); setKlientName(klientSearch.trim()); setKlientOpen(false); }}
                      className="w-full text-left px-3 py-2 bg-amber-50 hover:bg-amber-100 border-b border-amber-200"
                    >
                      <div className="text-sm font-medium text-amber-900">
                        <Plus className="inline w-3.5 h-3.5 mr-1" />
                        {klientJeNovy ? <>Použít „{klientSearch.trim()}" jako nového klienta (zatím není v DB)</> : <>Použít „{klientSearch.trim()}" (klient mimo DB)</>}
                      </div>
                    </button>
                  )}
                  {filteredKlienti.length === 0 && (!povolRucniJmeno || !klientSearch.trim()) && (
                    <div className="px-3 py-2.5 text-sm text-slate-500 italic">Žádný klient neodpovídá.</div>
                  )}
                  {filteredKlienti.map((k) => {
                    const isSel = k.notion_id === klientId;
                    return (
                      <button
                        key={k.notion_id}
                        type="button"
                        onClick={() => { setKlientId(k.notion_id); setKlientName(jmenoKlienta(k)); setKlientOpen(false); setKlientSearch(""); }}
                        className={`w-full text-left px-3 py-2 flex items-center gap-2.5 hover:bg-brand-50 ${isSel ? "bg-brand-100" : ""} transition-colors`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-800 truncate demo-sensitive">{jmenoKlienta(k)}</div>
                          {k.stav && <div className="text-[11px] text-slate-500">stav: {k.stav}</div>}
                        </div>
                        {isSel && <Check className="w-4 h-4 text-brand-600 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              )}
              {klientId && (
                <div className="mt-1.5"><span className="inline-flex max-w-full items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700"><Check className="w-3.5 h-3.5 shrink-0" /><span className="truncate demo-sensitive">Klient z DB: {klientName}</span></span></div>
              )}
              {!klientId && klientName.trim() && (
                <div className="mt-1.5"><span className="inline-flex max-w-full items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700"><span className="truncate demo-sensitive">{klientJeNovy ? "Nový klient (zatím ne v DB)" : "Klient mimo DB"}: {klientName.trim()}</span></span></div>
              )}
              {klientVolitelny && (
                <div className="text-[11px] text-slate-500 mt-1">Trenér uvidí jméno ve výplatě v pilulce, do faktury se nepropíše. Do popisu ho nepiš.</div>
              )}
            </div>
          )}

          {/* Popis */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">
              Popis {aktivniSablona && <span className="text-[11px] text-slate-400 font-normal normal-case">(z šablony, upravitelné)</span>}
            </label>
            <textarea
              value={popis}
              onChange={(e) => { setPopis(e.target.value); }}
              placeholder={isBonus ? "Např. Hodnocení na Google" : "Např. Záloha na výplatu. Půjčka 5000 Kč"}
              rows={2}
              className="field w-full resize-none"
            />
          </div>

          {/* Cilovy stav. Manager i owner volí stejně. Vyplacen jen owner. */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Cílový stav</label>
            <select
              value={stav}
              onChange={(e) => setStav(e.target.value)}
              disabled={klientBezLoginu}
              className="field w-full disabled:bg-slate-50 disabled:text-slate-500"
            >
              {STAV_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} disabled={klientBezLoginu && o.value !== "caka_splneni"}>{o.label}</option>
              ))}
              {isOwner && !klientBezLoginu && (
                <option key={STAV_OPTION_VYPLACEN.value} value={STAV_OPTION_VYPLACEN.value}>{STAV_OPTION_VYPLACEN.label}</option>
              )}
            </select>
            {klientBezLoginu && (
              <div className="text-[11px] text-amber-700 mt-1">Zamčeno na „Čeká na splnění podmínky", klient se zatím nepřihlásil do aplikace.</div>
            )}
            <div className="text-[11px] text-slate-500 mt-1">
              {[...STAV_OPTIONS, STAV_OPTION_VYPLACEN].find((o) => o.value === stav)?.hint}
            </div>
          </div>

          {/* Mesic vyplaceni jen pokud stav = vyplacen */}
          {stav === "vyplacen" && (
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Měsíc proplacení</label>
              <select
                value={mesicVyplaceni}
                onChange={(e) => setMesicVyplaceni(e.target.value)}
                className="field w-full"
              >
                {monthOptions.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
              </select>
            </div>
          )}

          {/* Poznamka */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Poznámka (volitelné)</label>
            <input
              type="text"
              value={poznamka}
              onChange={(e) => setPoznamka(e.target.value)}
              placeholder="např. faktura za březen, doplnit po telefonu"
              className="field w-full"
            />
          </div>
        </div>

    </Modal>
  );
}

// Avatar v comboboxu: foto s fallbackem na iniciály při broken image,
// + objectPosition 25% aby byl obličej ve středu kruhu (portretni fotky maji
// oblicej v horni casti kompozice).
function ComboAvatar({ person }) {
  const [failed, setFailed] = useState(false);
  const initials = String(person.display_name || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .filter(Boolean)
    .join("")
    .toUpperCase() || "?";
  if (person.foto && !failed) {
    return (
      <img
        src={person.foto}
        alt=""
        loading="lazy"
        className="w-7 h-7 rounded-full object-cover ring-1 ring-slate-200 shrink-0"
        style={{ objectPosition: "50% 25%" }}
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-slate-400 to-slate-600 text-white flex items-center justify-center text-[11px] font-bold shrink-0">
      {initials}
    </div>
  );
}
